/**
 * Which build of the app this phone is running, and when it was deployed.
 *
 * It is **baked into the bundle** at build time rather than asked of the
 * server, and that is the whole point: the line has to describe *the code in
 * your hand*. A phone one deploy behind that read the server's answer would
 * announce an update it does not have — the same class of lie that made a
 * deploy apply itself rather than offer (`platform/update.ts`), and the reason
 * that mattered (Het Woord bundles its word lists, so two builds play two
 * different words). It also costs no request, so it is still true in a shop
 * with no signal.
 *
 * `ops/deploy.sh` passes the stamp in as a docker build arg, so it moves on
 * every deploy; a plain `docker compose build`, a dev server or a test run
 * falls back to the moment the bundle itself was built, and to no commit.
 */

/*
 * Declared, never imported: `define` in `vite.config.ts` replaces these
 * identifiers textually. The `typeof` guard is what keeps the file safe
 * wherever that define is absent — a free identifier would otherwise be a
 * ReferenceError that takes the whole dashboard down for a footnote.
 */
declare const __BUILD_TIME__: string | undefined;
declare const __BUILD_COMMIT__: string | undefined;

export interface BuildStamp {
  /** When this bundle was built — the deploy that put it on the phone. */
  at: Date;
  /** The short commit it was built from, or null outside a real deploy. */
  commit: string | null;
}

export function buildStamp(): BuildStamp | null {
  const iso = typeof __BUILD_TIME__ === 'string' ? __BUILD_TIME__ : '';
  if (!iso) return null;

  const at = new Date(iso);
  // An unparseable stamp shows nothing rather than "Invalid Date".
  if (Number.isNaN(at.getTime())) return null;

  const commit = typeof __BUILD_COMMIT__ === 'string' ? __BUILD_COMMIT__ : '';
  return { at, commit: commit && commit !== 'dev' ? commit : null };
}

const TIME = new Intl.DateTimeFormat('nl-NL', { hour: '2-digit', minute: '2-digit' });
const DAY = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'long' });
const DAY_YEAR = new Intl.DateTimeFormat('nl-NL', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/**
 * The stamp as the household reads it: "vandaag om 14:32", "gisteren om 09:05",
 * "21 september om 14:32" — and the year too once it is no longer this one,
 * because "21 september" a year later is the wrong answer stated confidently.
 *
 * Counted in **calendar days**, not elapsed hours: a deploy at 23:50 is
 * "gisteren" at 00:10, which is how a person reads it, where "14 uur geleden"
 * would be both right and useless.
 */
export function describeBuildTime(at: Date, now: Date = new Date()): string {
  const days = Math.round((startOfDay(now) - startOfDay(at)) / 86_400_000);
  const clock = TIME.format(at);

  // days < 0 is a phone whose clock is behind the box's. Reading "morgen" off
  // that is worse than reading "vandaag".
  if (days <= 0) return `vandaag om ${clock}`;
  if (days === 1) return `gisteren om ${clock}`;

  const date = at.getFullYear() === now.getFullYear() ? DAY.format(at) : DAY_YEAR.format(at);
  return `${date} om ${clock}`;
}
